import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useSellerStatement } from '@/app/hooks/useSellerStatement'
import {
  SellerStatementGroup,
  SellerStatementItem,
} from '@/app/services/sellersService/sellerStatement'
import { selectedIdfromTableRowTransaction } from '@/app/store'
import { Spinner } from '@/view/components/ui/spinner'
import { EmptyStatement } from '@/view/components/EmptyStatement'
import { SellerStatementList } from './components/SellerStatementList'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/view/components/ui/sheet'
import TransactionDetails from '@/view/pages/Dashboard/components/TransactionTable/components/TransactionDetails'
import { SellerStatementFilter } from './components/SellerStatementFilter'

const mergeStatementGroups = (
  current: SellerStatementGroup[],
  incoming: SellerStatementGroup[],
) => {
  const groupsMap = new Map<string, SellerStatementGroup>()

  current.forEach((group) => {
    groupsMap.set(group.date, {
      ...group,
      transactions: [...group.transactions],
    })
  })

  incoming.forEach((group) => {
    const existingGroup = groupsMap.get(group.date)

    if (!existingGroup) {
      groupsMap.set(group.date, group)
      return
    }

    const transactionKeys = new Set(
      existingGroup.transactions.map(
        (transaction) =>
          `${transaction.contract}-${transaction.origin}-${transaction.type}-${transaction.status}-${transaction.statusAt}-${transaction.value}`,
      ),
    )

    const nextTransactions = [...existingGroup.transactions]

    group.transactions.forEach((transaction) => {
      const key = `${transaction.contract}-${transaction.origin}-${transaction.type}-${transaction.status}-${transaction.statusAt}-${transaction.value}`

      if (!transactionKeys.has(key)) {
        transactionKeys.add(key)
        nextTransactions.push(transaction)
      }
    })

    groupsMap.set(group.date, {
      ...existingGroup,
      transactions: nextTransactions,
    })
  })

  return Array.from(groupsMap.values()).sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  )
}

export const SellerStatementPage = () => {
  const { id } = useParams()
  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    setParams,
    resetParams,
    params,
  } = useSellerStatement(id)

  const [sheetOpen, setSheetOpen] = useState(false)
  const [, setSelectedTransactionId] = useAtom(
    selectedIdfromTableRowTransaction,
  )
  const observerRef = useRef<HTMLDivElement>(null)

  const statement = useMemo(
    () =>
      (data?.pages ?? []).reduce<SellerStatementGroup[]>(
        (current, page) => mergeStatementGroups(current, page.data),
        [],
      ),
    [data?.pages],
  )

  const canLoadMore = useMemo(
    () => Boolean(hasNextPage && !params.endDate),
    [hasNextPage, params.endDate],
  )

  const handleLoadMore = useCallback(() => {
    if (isFetchingNextPage || !canLoadMore) {
      return
    }

    fetchNextPage()
  }, [canLoadMore, fetchNextPage, isFetchingNextPage])

  useEffect(() => {
    if (!observerRef.current || !canLoadMore || isFetchingNextPage) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting) {
          handleLoadMore()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px',
      },
    )

    observer.observe(observerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [canLoadMore, handleLoadMore, isFetchingNextPage, statement.length])

  const handleTransactionClick = (transaction: SellerStatementItem) => {
    if (transaction.type === 'WITHDRAW') {
      return
    }

    setSelectedTransactionId(transaction.origin)
    setSheetOpen(true)
  }

  const handleFilter = useCallback(
    (filterParams: { startDate: string; endDate?: string }) => {
      setParams((state) => ({
        ...state,
        startDate: filterParams.startDate,
        endDate: filterParams.endDate,
        limit: filterParams.endDate ? undefined : state.limit,
      }))
    },
    [setParams],
  )

  const handleClearFilter = useCallback(() => {
    resetParams()
  }, [resetParams])

  if (isLoading && statement.length === 0) {
    return (
      <div className="flex h-[350px] items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      <div className="my-5 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Extrato de comissão</h2>
        <SellerStatementFilter
          onFilter={handleFilter}
          onClear={handleClearFilter}
        />
      </div>

      {!isFetching && statement.length === 0 ? (
        <EmptyStatement />
      ) : (
        <SellerStatementList
          data={statement}
          onTransactionClick={handleTransactionClick}
        />
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[340px] overflow-y-auto rounded-2xl bg-[#F4F5F7] p-2 xs:mr-5 xs:mt-5 xs:h-[95%] xs:w-96 xs:p-5">
          <div className="min-h-[800px]">
            <SheetHeader>
              <SheetTitle className="text-xl font-semibold">
                <h2>Detalhes da transação</h2>
                <div className="my-5 w-full border-b-[1px] border-gray01" />
              </SheetTitle>
            </SheetHeader>
            <TransactionDetails isClient />
          </div>
        </SheetContent>
      </Sheet>

      {canLoadMore && !isFetchingNextPage && statement.length > 0 && (
        <div ref={observerRef} className="h-1" />
      )}

      {isFetchingNextPage && statement.length > 0 && (
        <div className="flex justify-center">
          <Spinner size="small" />
        </div>
      )}
    </div>
  )
}
